import { computed, reactive, ref, watch, type Ref } from "vue";
import { getCompositionAutocomplete, getReferenceData } from "@/api/shooting";
import { getCategoriesTree } from "@/api/categories";
import { getCouleursOfficielles } from "@/api/couleurs";
import { getVendeurMarques } from "@/api/brands";
import {
  getShopifyColorMappings,
  getShopifyProduct,
  getShopifyProductVariantStocks,
  resetShopifyProductOverrides,
  resolveShopifyCoverImageUrl,
  saveShopifyCategoryMapping,
  saveShopifyColorMapping,
  saveShopifyProductOverrides,
} from "@/api/shopify";
import {
  parsePackQuantities,
  parsePositivePrice,
  parsePositiveWeight,
  getMelUnitWeightError,
  validateCompositionsForSave,
} from "@/utils/melCompositions";
import { rememberCompositionSet } from "@/utils/melCompositionHistory";
import { brandSelectOptions } from "@/utils/sellerBrand";
import {
  formatDeclinaisonLabel,
  getDeclinaisonLength,
} from "@/utils/declinaisonLabel";
import type { Brand } from "@/types/brand";
import type {
  PrestashopImageOverride,
  PrestashopMelOverrides,
  PrestashopReferenceData,
} from "@/types/prestashop";
import type {
  ShopifyCatalogPage,
  ShopifyProduct,
  ShopifyVariantStock,
} from "@/types/shopify";
import type {
  CouleurAPI,
  CouleurSelectorItem,
  CompositionAutocompleteOption,
  CompositionItem,
  CompositionLocalisation,
  CompositionMateriau,
  CompositionType,
} from "@/types/mel-components";
import type { CategorieNode, CategoryPathSelection } from "@/types/categorie";

const option = (id: string | number, label: string) => ({
  value: String(id),
  label,
});

function countCsvParts(value: string | number | undefined | null) {
  return String(value || "")
    .split(",")
    .map((part) => part.trim())
    .filter(Boolean).length;
}

function findCategoryPath(
  nodes: CategorieNode[],
  targetId: string,
  path: string[] = [],
): string[] | null {
  for (const node of nodes) {
    const nextPath = [...path, String(node.id_categorie)];
    if (String(node.id_categorie) === targetId) return nextPath;
    const childPath = findCategoryPath(node.children || [], targetId, nextPath);
    if (childPath) return childPath;
  }
  return null;
}

function normalizeStockLabel(value: string) {
  return value
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "");
}

const OUTERWEAR_WEIGHT_KEYWORDS = [
  "exterieur",
  "manteau",
  "doudoune",
  "parka",
  "trench",
  "blouson",
  "caban",
  "bomber",
  "fourrure",
  "cape",
  "poncho",
  "coat",
  "outerwear",
];

function isOuterwearCategoryLabel(value: string): boolean {
  const haystack = String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();
  return OUTERWEAR_WEIGHT_KEYWORDS.some((keyword) => haystack.includes(keyword));
}

function collectCategoryLabels(
  nodes: CategorieNode[],
  targetId: string,
  pathLabels: string[] = [],
): string[] | null {
  for (const node of nodes) {
    const nextLabels = [...pathLabels, String(node.label || "")];
    if (String(node.id_categorie) === targetId) return nextLabels;
    const child = collectCategoryLabels(
      node.children || [],
      targetId,
      nextLabels,
    );
    if (child) return child;
  }
  return null;
}

export function useShopifyMelProductSheet(options: {
  catalog: Ref<ShopifyCatalogPage>;
  onReloadCatalog: () => Promise<void>;
  success: (message: string) => void;
  error: (message: string) => void;
}) {
  const editedProduct = ref<ShopifyProduct | null>(null);
  const savingProduct = ref(false);
  const resettingProduct = ref(false);
  const openingProduct = ref(false);
  const productImageOverrides = ref<PrestashopImageOverride[]>([]);
  const referenceData = ref<PrestashopReferenceData | null>(null);
  const categoriesTree = ref<CategorieNode[]>([]);
  const officialColors = ref<CouleurAPI[]>([]);
  const linkedSourceLabelsByColorId = ref<Record<number, string[]>>({});
  const sellerBrands = ref<Brand[]>([]);
  const selectedColors = ref<CouleurSelectorItem[]>([]);
  const colorSourceChoices = ref<
    Array<{ sourceName: string; colorId: number }>
  >([]);
  const productVariantStocks = ref<ShopifyVariantStock[]>([]);
  let syncingColorStocks = false;
  const selectedCompositions = ref<CompositionItem[]>([]);
  let openProductSession = 0;
  const compositionType = ref<CompositionType>("autres");
  const compositionLocations = ref<CompositionLocalisation[]>([]);
  const shoeCompositions = ref<CompositionMateriau[]>([]);
  const compositionAutocompleteOptions = ref<CompositionAutocompleteOption[]>(
    [],
  );
  const showCompositionDropdown = ref(false);
  const isLoadingCompositionAutocomplete = ref(false);
  const selectedCategoryLevel1 = ref("");
  const selectedCategoryLevel2 = ref("");
  const selectedCategoryLevel3 = ref("");
  const productShopifyPriceHint = ref<number | null>(null);
  const productWholesalePriceHint = ref<number | null>(null);
  const productShopifyWeightHint = ref<number | null>(null);

  const form = reactive({
    brandId: "",
    categoryId: "",
    collectionId: "",
    provenanceId: "",
    price: "",
    weight: "",
    soldBy: "tailles",
    packId: "",
    sizeRangeId: "",
    stock: "",
  });

  const brandOptions = computed(() => brandSelectOptions(sellerBrands.value));
  const collectionOptions = computed(() =>
    (referenceData.value?.collections || []).map((item) =>
      option(item.id, item.label || String(item.id)),
    ),
  );
  const provenanceOptions = computed(() =>
    (referenceData.value?.provenances || []).map((item) =>
      option(item.id, item.label || String(item.id)),
    ),
  );
  const packOptions = computed(() => packOptionsForSizeRange(form.sizeRangeId));
  const sizeRangeOptions = computed(() =>
    (referenceData.value?.declinaisons || []).map((item) =>
      option(
        item.id,
        formatDeclinaisonLabel(item.label, item.tailles) || String(item.id),
      ),
    ),
  );
  const categoryLevel1Options = computed(() =>
    categoriesTree.value.map((item) =>
      option(item.id_categorie, item.label || String(item.id_categorie)),
    ),
  );
  const categoryLevel2Options = computed(() => {
    if (!selectedCategoryLevel1.value) return [];
    const level1 = categoriesTree.value.find(
      (item) => String(item.id_categorie) === selectedCategoryLevel1.value,
    );
    return (level1?.children || []).map((item) =>
      option(item.id_categorie, item.label || String(item.id_categorie)),
    );
  });
  const categoryLevel3Options = computed(() => {
    if (!selectedCategoryLevel1.value || !selectedCategoryLevel2.value) {
      return [];
    }
    const level1 = categoriesTree.value.find(
      (item) => String(item.id_categorie) === selectedCategoryLevel1.value,
    );
    const level2 = level1?.children?.find(
      (item) => String(item.id_categorie) === selectedCategoryLevel2.value,
    );
    return (level2?.children || []).map((item) =>
      option(item.id_categorie, item.label || String(item.id_categorie)),
    );
  });
  const selectedSizeNames = computed(() => {
    const range = (referenceData.value?.declinaisons || []).find(
      (item) => String(item.id) === form.sizeRangeId,
    );
    return String(range?.tailles || "")
      .split(",")
      .map((size) => size.trim())
      .filter(Boolean);
  });
  const soldByOptions = [
    option("tailles", "Tailles aux choix"),
    option("couleurs", "À la couleur"),
    option("melangees", "Couleurs mélangées"),
  ];
  const normalizedCompositionsForSave = computed(() => {
    if (compositionType.value !== "chaussures") {
      return selectedCompositions.value.map((composition) => ({
        compositionId: composition.compositionId,
        locationId: composition.localisationId,
        percentage: Number(composition.percentage) || 0,
      }));
    }
    const countByLocation = new Map<number, number>();
    for (const composition of selectedCompositions.value) {
      countByLocation.set(
        composition.localisationId,
        (countByLocation.get(composition.localisationId) || 0) + 1,
      );
    }
    return selectedCompositions.value.map((composition) => ({
      compositionId: composition.compositionId,
      locationId: composition.localisationId,
      percentage: 100 / (countByLocation.get(composition.localisationId) || 1),
    }));
  });
  const melangeesPackMismatchMessage = computed(() => {
    if (form.soldBy !== "melangees" || !form.packId) return "";
    if (!selectedColors.value.length) return "";
    const selectedPack = (referenceData.value?.packs || []).find(
      (pack) => String(pack.id) === String(form.packId),
    );
    const packQuantites = parsePackQuantities(selectedPack?.value);
    if (!packQuantites.length) return "";

    const sommeQuantites = new Array(packQuantites.length).fill(0);
    for (const color of selectedColors.value) {
      (color.quantitesParTaille || []).forEach((quantity, index) => {
        if (index < sommeQuantites.length) {
          sommeQuantites[index] += Number(quantity) || 0;
        }
      });
    }

    for (let index = 0; index < packQuantites.length; index += 1) {
      if (packQuantites[index] !== sommeQuantites[index]) {
        return `La somme des quantités des couleurs (${sommeQuantites.join(
          "-",
        )}) ne correspond pas aux quantités du paquet (${packQuantites.join(
          "-",
        )})`;
      }
    }
    return "";
  });
  const productFormMissingFields = computed(() => {
    const product = editedProduct.value;
    if (!product) return [];
    const missing: string[] = [];
    if (!product.reference) missing.push("Référence");
    if (!product.name) missing.push("Nom");
    if (!parsePositivePrice(form.price)) missing.push("Prix");
    const weightError = getMelUnitWeightError(form.weight);
    if (weightError) missing.push(weightError.replace(/\.$/, ""));
    if (!productImageOverrides.value.length) {
      missing.push("Photo (obligatoire pour la mise en ligne)");
    } else if (selectedColors.value.length) {
      const normalize = (value: string | null | undefined) =>
        value?.trim().toLocaleLowerCase("fr") ?? "";
      const colorsWithoutPhoto = selectedColors.value
        .filter((color) => {
          return !productImageOverrides.value.some((image) => {
            if (color.id && Number(image.colorId) === Number(color.id)) {
              return true;
            }
            if (
              Boolean(image.colorName?.trim()) &&
              normalize(image.colorName) === normalize(color.nom)
            ) {
              return true;
            }
            const source = (product.images ?? []).find(
              (item) => String(item.id) === String(image.id),
            );
            const sourceName =
              source?.sourceColorName || source?.colorName || image.colorName;
            return colorSourceChoices.value.some(
              (choice) =>
                Number(choice.colorId) === Number(color.id) &&
                normalize(choice.sourceName) === normalize(sourceName),
            );
          });
        })
        .map((color) => color.nom);
      if (colorsWithoutPhoto.length) {
        missing.push(`Photo manquante pour : ${colorsWithoutPhoto.join(", ")}`);
      }
    }
    const pendingCategoriesList = editedProduct.value?.pendingCategories ?? [];
    if (!hasEfashionCategorySelection.value) {
      if (pendingCategoriesList.length) {
        missing.push("Catégorie eFashion (lier ou choisir ci-dessous)");
      } else {
        missing.push("Catégorie eFashion");
      }
    }
    if (!form.brandId) missing.push("Marque");
    if (!form.collectionId) missing.push("Collection");
    if (!form.provenanceId) missing.push("Provenance");

    const compositions = normalizedCompositionsForSave.value;
    const totalsByLocation = new Map<number, number>();
    for (const composition of compositions) {
      totalsByLocation.set(
        composition.locationId,
        (totalsByLocation.get(composition.locationId) || 0) +
          composition.percentage,
      );
    }
    const compositionsValid =
      compositions.length > 0 &&
      compositions.every((composition) => composition.percentage > 0) &&
      Array.from(totalsByLocation.values()).every(
        (total) => Math.abs(total - 100) <= 0.01,
      ) &&
      (compositionType.value !== "chaussures" ||
        compositionLocations.value.every((location) =>
          compositions.some(
            (composition) =>
              composition.locationId === location.id_composition_localisation,
          ),
        ));
    if (!compositions.length) {
      missing.push("Composition");
    } else if (!compositionsValid) {
      missing.push("Pourcentages de composition");
    }

    if (!selectedColors.value.length) missing.push("Couleur");
    const pendingColors = editedProduct.value?.pendingColors ?? [];
    if (pendingColors.length) {
      missing.push(
        `Couleurs à lier : ${pendingColors.join(", ")}`,
      );
    }
    const collisionMessage = collisionBlockMessage();
    if (collisionMessage) missing.push(collisionMessage);
    if (
      form.soldBy !== "couleurs" &&
      form.soldBy !== "melangees" &&
      form.soldBy !== "tailles"
    ) {
      missing.push("Mode de vente (saisie manuelle)");
    }
    if (!form.sizeRangeId) missing.push("Déclinaison");
    if (form.soldBy && form.soldBy !== "tailles" && !form.packId) {
      missing.push("Pack");
    }
    if (
      form.soldBy === "melangees" &&
      (!selectedColors.value.length ||
        selectedColors.value.some(
          (color) =>
            !color.quantitesParTaille?.some((quantity) => Number(quantity) > 0),
        ))
    ) {
      missing.push("Quantités des couleurs mélangées");
    }
    if (melangeesPackMismatchMessage.value) {
      missing.push("Somme des quantités ≠ pack");
    }
    return missing;
  });
  const canSaveProductForm = computed(
    () => productFormMissingFields.value.length === 0,
  );

  function packOptionsForSizeRange(sizeRangeId: string) {
    if (!sizeRangeId) return [];
    const selectedDeclinaison = (referenceData.value?.declinaisons || []).find(
      (item) => String(item.id) === String(sizeRangeId),
    );
    const tailleCount = getDeclinaisonLength(selectedDeclinaison?.tailles);
    if (!tailleCount) return [];
    return (referenceData.value?.packs || [])
      .filter((pack) => countCsvParts(pack.value) === tailleCount)
      .map((item) =>
        option(
          item.id,
          item.value
            ? `${item.label || item.id} (${item.value})`
            : item.label || String(item.id),
        ),
      );
  }

  function matchesStockColor(
    stockColorName: string | null,
    colorName: string,
  ): boolean {
    if (!stockColorName) return selectedColors.value.length === 1;
    return (
      normalizeStockLabel(stockColorName) === normalizeStockLabel(colorName)
    );
  }

  function quantityForColorSize(
    colorName: string,
    sizeName: string,
  ): number | null {
    const sizeKey = normalizeStockLabel(sizeName);
    let total = 0;
    let found = false;
    for (const stock of productVariantStocks.value) {
      if (!matchesStockColor(stock.colorName, colorName)) continue;
      const stockSizeKey = stock.sizeName
        ? normalizeStockLabel(stock.sizeName)
        : "tu";
      if (stockSizeKey !== sizeKey && !(sizeKey === "tu" && !stock.sizeName)) {
        continue;
      }
      found = true;
      total += stock.quantity;
    }
    return found ? total : null;
  }

  function quantityForColor(colorName: string): number | null {
    let total = 0;
    let found = false;
    for (const stock of productVariantStocks.value) {
      if (!matchesStockColor(stock.colorName, colorName)) continue;
      found = true;
      total += stock.quantity;
    }
    return found ? total : null;
  }

  function syncMelangeesPackStock(preferExisting = true) {
    if (form.soldBy !== "melangees") return;
    if (preferExisting && form.stock !== "") return;
    if (!productVariantStocks.value.length) return;
    form.stock = String(
      productVariantStocks.value.reduce(
        (total, stock) => total + stock.quantity,
        0,
      ),
    );
  }

  function syncColorStocks(preferExisting = true) {
    if (syncingColorStocks) return;
    syncingColorStocks = true;
    try {
      if (selectedColors.value.length) {
        selectedColors.value = selectedColors.value.map((color) => {
          if (form.soldBy === "tailles") {
            const sizes = selectedSizeNames.value;
            if (!sizes.length) {
              return { ...color, tailleStocks: undefined, stock: undefined };
            }
            const existingBySize = new Map(
              (color.tailleStocks || []).map((item) => [
                normalizeStockLabel(item.taille),
                item.quantity,
              ]),
            );
            return {
              ...color,
              stock: undefined,
              tailleStocks: sizes.map((taille) => {
                const key = normalizeStockLabel(taille);
                const existing = existingBySize.get(key);
                if (preferExisting && existing != null) {
                  return { taille, quantity: existing };
                }
                const fromShopify = quantityForColorSize(color.nom, taille);
                if (fromShopify != null) {
                  return { taille, quantity: fromShopify };
                }
                return {
                  taille,
                  quantity: existing !== undefined ? existing : null,
                };
              }),
            };
          }
          if (form.soldBy === "couleurs") {
            const fromShopify = quantityForColor(color.nom);
            return {
              ...color,
              tailleStocks: undefined,
              stock:
                preferExisting && color.stock != null
                  ? color.stock
                  : fromShopify != null
                  ? fromShopify
                  : color.stock ?? null,
            };
          }
          return { ...color, tailleStocks: undefined, stock: undefined };
        });
      }
      syncMelangeesPackStock(preferExisting);
    } finally {
      syncingColorStocks = false;
    }
  }

  function setTailleStockQuantity(
    colorId: number,
    index: number,
    rawValue: string,
  ) {
    selectedColors.value = selectedColors.value.map((color) => {
      if (color.id !== colorId || !color.tailleStocks?.[index]) return color;
      const next = color.tailleStocks.map((item, itemIndex) =>
        itemIndex === index
          ? {
              ...item,
              quantity:
                rawValue === "" ? null : Math.max(0, Number(rawValue) || 0),
            }
          : item,
      );
      return { ...color, tailleStocks: next };
    });
  }

  function setColorStock(colorId: number, rawValue: string) {
    selectedColors.value = selectedColors.value.map((color) =>
      color.id === colorId
        ? {
            ...color,
            stock: rawValue === "" ? null : Math.max(0, Number(rawValue) || 0),
          }
        : color,
    );
  }

  async function loadProductVariantStocks(productGid: string) {
    try {
      const response = await getShopifyProductVariantStocks(productGid);
      if (editedProduct.value?.id !== productGid) return;
      productVariantStocks.value = response.stocks || [];
      syncColorStocks(true);
    } catch {
      if (editedProduct.value?.id === productGid) {
        productVariantStocks.value = [];
        syncColorStocks(true);
      }
    }
  }

  function syncProductShopifyPriceHints() {
    const product = editedProduct.value;
    if (!product) {
      productShopifyPriceHint.value = null;
      productWholesalePriceHint.value = null;
      productShopifyWeightHint.value = null;
      return;
    }
    const shopifyPrice = product.shopifyPrice ?? 0;
    const wholesalePrice = product.wholesalePrice ?? 0;
    const currentPrice = parsePositivePrice(form.price);
    productShopifyPriceHint.value =
      shopifyPrice > 0 &&
      currentPrice !== undefined &&
      Math.abs(currentPrice - shopifyPrice) > 0.001
        ? shopifyPrice
        : null;
    productWholesalePriceHint.value =
      wholesalePrice > 0 &&
      currentPrice !== undefined &&
      Math.abs(currentPrice - wholesalePrice) > 0.001
        ? wholesalePrice
        : null;
    const shopifyWeight = Number(product.shopifyWeight) || 0;
    const currentWeight = parsePositiveWeight(form.weight);
    productShopifyWeightHint.value =
      shopifyWeight > 0 &&
      currentWeight !== undefined &&
      Math.abs(currentWeight - shopifyWeight) > 0.0005
        ? shopifyWeight
        : null;
  }

  function restoreCategoryPath(categoryId: string) {
    const path = categoryId
      ? findCategoryPath(categoriesTree.value, categoryId)
      : null;
    selectedCategoryLevel1.value = path?.[0] || "";
    selectedCategoryLevel2.value = path?.[1] || "";
    selectedCategoryLevel3.value = path?.[2] || "";
  }

  function configureCompositionReferences(clearSelection = false) {
    const categoryId = selectedCategoryLevel1.value || form.categoryId;
    const isShoe =
      categoryId.startsWith("14") ||
      ["130106", "130306", "130405"].includes(form.categoryId);
    compositionType.value = isShoe ? "chaussures" : "autres";
    const categoryFilter = isShoe ? "14" : null;
    const references = referenceData.value?.compositions || [];
    compositionLocations.value = references
      .filter(
        (item) =>
          item.type === "localisation" &&
          (item.id_categorie ?? null) === categoryFilter,
      )
      .map((item) => ({
        id_composition_localisation: Number(item.id),
        libelle: item.libelle || item.label || String(item.id),
      }));
    shoeCompositions.value = isShoe
      ? references
          .filter(
            (item) =>
              item.type === "composition" &&
              String(item.id_categorie || "") === "14",
          )
          .map((item) => ({
            id_composition: Number(item.id),
            libelle: item.libelle || item.label || String(item.id),
          }))
      : [];
    if (clearSelection) selectedCompositions.value = [];
  }

  function syncCategoryIdFromLevels() {
    if (selectedCategoryLevel3.value) {
      form.categoryId = selectedCategoryLevel3.value;
      syncAutoWeightFromCategory();
      return;
    }
    if (selectedCategoryLevel2.value) {
      form.categoryId = categoryLevel3Options.value.length
        ? ""
        : selectedCategoryLevel2.value;
      syncAutoWeightFromCategory();
      return;
    }
    if (selectedCategoryLevel1.value) {
      form.categoryId = categoryLevel2Options.value.length
        ? ""
        : selectedCategoryLevel1.value;
      syncAutoWeightFromCategory();
      return;
    }
    form.categoryId = "";
    syncAutoWeightFromCategory();
  }

  /** Si le poids est encore le défaut 0,4 kg et la catégorie est extérieur/manteaux → 2 kg. */
  function syncAutoWeightFromCategory(pathLabel = "") {
    const shopifyWeight = Number(editedProduct.value?.shopifyWeight) || 0;
    if (shopifyWeight > 0) return;
    const current = parsePositiveWeight(form.weight);
    // Poids déjà saisi manuellement (≠ 0,4) → ne pas écraser.
    if (current !== undefined && Math.abs(current - 0.4) > 0.0005) return;

    const labels = form.categoryId
      ? collectCategoryLabels(categoriesTree.value, form.categoryId) || []
      : [];
    const product = editedProduct.value;
    const haystack = [
      pathLabel,
      ...labels,
      product?.categoryName,
      product?.shopifyCategoryName,
      product?.productType,
      product?.name,
      ...(product?.tags || []),
    ]
      .filter(Boolean)
      .join(" ");
    form.weight = isOuterwearCategoryLabel(haystack) ? "2" : "0.4";
  }

  function onCategoryPathSelect(selection: CategoryPathSelection) {
    selectedCategoryLevel1.value = selection.catId;
    selectedCategoryLevel2.value = selection.sousId;
    selectedCategoryLevel3.value = selection.sousSousId;
    form.categoryId = selection.leafId;
    configureCompositionReferences(true);
    syncAutoWeightFromCategory(selection.pathLabel || "");
  }

  function onCategoryLevel1Change(value?: string) {
    if (value !== undefined) selectedCategoryLevel1.value = String(value || "");
    selectedCategoryLevel2.value = "";
    selectedCategoryLevel3.value = "";
    syncCategoryIdFromLevels();
    configureCompositionReferences(true);
  }

  function onCategoryLevel2Change(value?: string) {
    if (value !== undefined) selectedCategoryLevel2.value = String(value || "");
    selectedCategoryLevel3.value = "";
    syncCategoryIdFromLevels();
    configureCompositionReferences(true);
  }

  function onCategoryLevel3Change(value?: string) {
    if (value !== undefined) selectedCategoryLevel3.value = String(value || "");
    syncCategoryIdFromLevels();
    configureCompositionReferences(true);
  }

  function onCategoryPathClear() {
    selectedCategoryLevel1.value = "";
    selectedCategoryLevel2.value = "";
    selectedCategoryLevel3.value = "";
    form.categoryId = "";
    configureCompositionReferences(true);
  }

  async function handleCompositionInput(
    value: string,
    localisationId: string | null,
  ) {
    const term = value.trim();
    if (!term) {
      compositionAutocompleteOptions.value = [];
      showCompositionDropdown.value = false;
      return;
    }
    try {
      isLoadingCompositionAutocomplete.value = true;
      const selectedIds = selectedCompositions.value
        .filter((composition) =>
          localisationId
            ? composition.localisationId === Number(localisationId)
            : true,
        )
        .map((composition) => composition.compositionId)
        .join("#");
      compositionAutocompleteOptions.value = await getCompositionAutocomplete(
        term,
        selectedIds,
      );
      showCompositionDropdown.value =
        compositionAutocompleteOptions.value.length > 0;
    } catch {
      compositionAutocompleteOptions.value = [];
      showCompositionDropdown.value = false;
    } finally {
      isLoadingCompositionAutocomplete.value = false;
    }
  }

  function selectComposition() {
    compositionAutocompleteOptions.value = [];
    showCompositionDropdown.value = false;
  }

  function hideCompositionDropdown() {
    window.setTimeout(() => {
      showCompositionDropdown.value = false;
    }, 200);
  }

  function syncCatalogProductImages(
    productGid: string,
    images: PrestashopImageOverride[],
  ) {
    const patchProduct = (product: ShopifyProduct): ShopifyProduct =>
      product.id === productGid
        ? {
            ...product,
            overrides: {
              ...(product.overrides || {}),
              images,
            },
            photosCount: images.length,
            imageUrl:
              resolveShopifyCoverImageUrl({
                ...product,
                overrides: {
                  ...(product.overrides || {}),
                  images,
                },
              }) ?? product.imageUrl,
          }
        : product;

    options.catalog.value = {
      ...options.catalog.value,
      items: options.catalog.value.items.map(patchProduct),
      newProducts: options.catalog.value.newProducts.map(patchProduct),
    };
  }

  function onProductImagesUpdate(images: PrestashopImageOverride[]) {
    productImageOverrides.value = images;
    if (!editedProduct.value) return;
    editedProduct.value = {
      ...editedProduct.value,
      overrides: {
        ...(editedProduct.value.overrides || {}),
        images,
      },
      photosCount: images.length,
    };
    syncCatalogProductImages(editedProduct.value.id, images);
    syncMainColorFromCoverPhotos();
  }

  /** Couleur principale = première photo du support multimédia qui a une couleur. */
  function syncMainColorFromCoverPhotos() {
    if (!selectedColors.value.length) return;
    const colorIds = new Set(
      selectedColors.value.map((color) => Number(color.id)),
    );
    const normalize = (value: string | null | undefined) =>
      value?.trim().toLocaleLowerCase("fr") ?? "";

    const matchColorId = (
      colorId: number | null | undefined,
      colorName: string | null | undefined,
    ): number | null => {
      const byId = Number(colorId);
      if (Number.isInteger(byId) && byId > 0 && colorIds.has(byId)) {
        return byId;
      }
      const needle = normalize(colorName);
      if (!needle) return null;
      const byOfficial = selectedColors.value.find(
        (color) => normalize(color.nom) === needle,
      );
      if (byOfficial) return Number(byOfficial.id);
      for (const color of selectedColors.value) {
        const sources = linkedSourceLabelsByColorId.value[Number(color.id)] || [];
        if (sources.some((label) => normalize(label) === needle)) {
          return Number(color.id);
        }
      }
      return null;
    };

    let mainId: number | null = null;
    for (const image of editedProduct.value?.images ?? []) {
      mainId = matchColorId(null, image.colorName);
      if (mainId) break;
    }
    if (!mainId) {
      for (const image of productImageOverrides.value) {
        mainId = matchColorId(image.colorId, image.colorName);
        if (mainId) break;
      }
    }
    if (!mainId) return;
    if (
      selectedColors.value.some(
        (color) => color.isMain && Number(color.id) === mainId,
      )
    ) {
      return;
    }
    selectedColors.value = selectedColors.value.map((color) => ({
      ...color,
      isMain: Number(color.id) === mainId,
    }));
  }

  function onProductImagesSaved(images: PrestashopImageOverride[]) {
    onProductImagesUpdate(images);
    options.success("Sélection de photos enregistrée.");
  }

  function buildInitialImageOverrides(product: ShopifyProduct) {
    if (product.overrides?.images?.length) {
      return product.overrides.images.map((image) => ({
        ...image,
        colorId: image.colorId ?? null,
        colorName: image.colorName ?? null,
      }));
    }
    const normalizeUrl = (url: string | null | undefined) => {
      const raw = String(url || "").trim();
      if (!raw) return "";
      try {
        const parsed = new URL(raw);
        return `${parsed.origin}${parsed.pathname}`.toLowerCase();
      } catch {
        return raw.split("?")[0].toLowerCase();
      }
    };
    const colored = product.images
      .filter((image) => Boolean(image.colorName?.trim()))
      .map((image) => ({
        type: "shopify" as const,
        id: image.id,
        url: image.url,
        rotation: 0,
        colorId: null as number | null,
        colorName: image.colorName ?? null,
      }));
    const coverKey = normalizeUrl(product.imageUrl);
    if (!coverKey || colored.length < 2) return colored;
    const coverIndex = colored.findIndex(
      (image) => normalizeUrl(image.url) === coverKey,
    );
    if (coverIndex <= 0) return colored;
    const next = [...colored];
    const [cover] = next.splice(coverIndex, 1);
    return [cover, ...next];
  }

  async function openProduct(product: ShopifyProduct) {
    const session = ++openProductSession;
    openingProduct.value = true;
    editedProduct.value = product;
    selectedCompositions.value = [];
    selectedColors.value = [];
    colorSourceChoices.value = [];
    productShopifyPriceHint.value = null;
    productWholesalePriceHint.value = null;
    productShopifyWeightHint.value = null;
    productImageOverrides.value = [];
    productVariantStocks.value = [];
    void loadProductVariantStocks(product.id);

    let workingProduct = product;
    try {
      const detailed = await getShopifyProduct(product.id);
      if (session !== openProductSession) return;
      workingProduct = {
        ...product,
        ...detailed,
        overrides: {
          ...(detailed.overrides || {}),
          ...(product.overrides || {}),
        },
        images: detailed.images?.length ? detailed.images : product.images,
      };
      editedProduct.value = workingProduct;
      colorSourceChoices.value = (
        workingProduct.overrides?.colorSourceChoices ?? []
      ).flatMap((choice) => {
        const sourceName = String(choice?.sourceName || "").trim();
        const colorId = Number(choice?.colorId);
        if (!sourceName || !Number.isInteger(colorId) || colorId <= 0) return [];
        return [{ sourceName, colorId }];
      });
      productImageOverrides.value = buildInitialImageOverrides(workingProduct);
    } catch (caught) {
      if (session !== openProductSession) return;
      openingProduct.value = false;
      options.error(
        caught instanceof Error
          ? caught.message
          : "Impossible de charger le détail Shopify (photos par couleur)."
      );
      editedProduct.value = null;
      return;
    }

    const values = workingProduct.overrides || {};
    form.brandId = "";
    form.categoryId = values.categoryId ? String(values.categoryId) : "";
    form.collectionId = values.collectionId ? String(values.collectionId) : "";
    form.provenanceId = values.provenanceId ? String(values.provenanceId) : "";
    const effectivePrice =
      values.price && values.price > 0
        ? values.price
        : workingProduct.price > 0
        ? workingProduct.price
        : workingProduct.wholesalePrice && workingProduct.wholesalePrice > 0
        ? workingProduct.wholesalePrice
        : workingProduct.shopifyPrice ?? 0;
    form.price = effectivePrice > 0 ? String(effectivePrice) : "";
    const effectiveWeight =
      values.weight && values.weight > 0
        ? values.weight
        : workingProduct.weight > 0
        ? workingProduct.weight
        : 0;
    form.weight = effectiveWeight > 0 ? String(effectiveWeight) : "";
    syncProductShopifyPriceHints();
    form.soldBy =
      values.soldBy === "couleurs" ||
      values.soldBy === "melangees" ||
      values.soldBy === "tailles"
        ? values.soldBy
        : "tailles";
    form.packId = values.packId ? String(values.packId) : "";
    form.sizeRangeId = values.sizeRangeId ? String(values.sizeRangeId) : "";
    form.stock =
      values.stock !== undefined && values.stock !== null
        ? String(values.stock)
        : "";
    try {
      const [references, tree, colors, brands, colorMappings] = await Promise.all([
        getReferenceData(),
        categoriesTree.value.length
          ? Promise.resolve(categoriesTree.value)
          : getCategoriesTree("fr"),
        getCouleursOfficielles(),
        getVendeurMarques(),
        getShopifyColorMappings().catch(() => []),
      ]);
      if (session !== openProductSession) return;
      if (editedProduct.value?.id !== workingProduct.id) return;
      referenceData.value = references;
      categoriesTree.value = tree;
      officialColors.value = colors;
      applyColorMappings(colorMappings);
      sellerBrands.value = brands;
      form.brandId = values.brandId && brands.some((brand) => Number(brand.id) === Number(values.brandId))
        ? String(values.brandId) : "";
      if (!form.collectionId) {
        const allSeasons = (references.collections || []).find(
          (item: { id: number; label: string }) => {
            const label = String(item.label || "")
              .toLowerCase()
              .trim();
            return label.includes("toutes") && label.includes("saisons");
          },
        );
        if (allSeasons?.id) {
          form.collectionId = String(allSeasons.id);
        }
      }
      if (!form.provenanceId) {
        const china = (references.provenances || []).find(
          (item: { id: number; label?: string; libelle?: string }) => {
            const label = String(item.label || item.libelle || "")
              .normalize("NFD")
              .replace(/[\u0300-\u036f]/g, "")
              .toLowerCase()
              .trim();
            return (
              label === "chine" ||
              label === "china" ||
              label.includes("chine") ||
              label.includes("china")
            );
          },
        );
        if (china?.id) {
          form.provenanceId = String(china.id);
        }
      }
      restoreCategoryPath(form.categoryId);
      configureCompositionReferences();
      const compositions = values.compositions?.length
        ? values.compositions
        : values.compositionId &&
          values.compositionLocationId &&
          values.compositionPercentage
        ? [
            {
              compositionId: values.compositionId,
              locationId: values.compositionLocationId,
              percentage: values.compositionPercentage,
            },
          ]
        : [];
      if (selectedCompositions.value.length === 0) {
        selectedCompositions.value = compositions.map((composition) => {
          const reference = (referenceData.value?.compositions || []).find(
            (item) =>
              item.type === "composition" &&
              Number(item.id) === Number(composition.compositionId),
          );
          return {
            compositionId: Number(composition.compositionId),
            localisationId: Number(composition.locationId),
            compositionName:
              reference?.libelle ||
              reference?.label ||
              `Composition ${composition.compositionId}`,
            percentage: Number(composition.percentage),
          };
        });
      }
      const selectedIds = values.colorIds?.length
        ? values.colorIds
        : values.colorId
        ? [values.colorId]
        : [];
      const savedColors: NonNullable<PrestashopMelOverrides["colors"]> = values
        .colors?.length
        ? values.colors
        : (selectedIds.map((colorId, index) => ({
            colorId,
            isMain: index === 0,
          })) as NonNullable<PrestashopMelOverrides["colors"]>);
      if (selectedColors.value.length === 0) {
        selectedColors.value = savedColors.flatMap((savedColor, index) => {
          const color = officialColors.value.find(
            (item) => Number(item.id_couleur) === Number(savedColor.colorId),
          );
          const sizeStocks = savedColor.sizeStocks;
          return color
            ? [
                {
                  id: Number(color.id_couleur),
                  nom: color.couleur_FR,
                  isMain: savedColor.isMain || index === 0,
                  hexColor: color.hexColor || null,
                  quantitesParTaille: savedColor.quantitiesBySize,
                  tailleStocks: sizeStocks?.map((item) => ({
                    taille: item.size,
                    quantity: item.quantity,
                  })),
                  stock:
                    savedColor.stock !== undefined && savedColor.stock !== null
                      ? savedColor.stock
                      : null,
                },
              ]
            : [];
        });
        syncMainColorFromCoverPhotos();
      }
      dropAbandonedMappedColors();
      syncColorStocks(true);
    } catch (caught) {
      options.error(
        caught instanceof Error
          ? caught.message
          : "Référentiels eFashion indisponibles.",
      );
    } finally {
      if (session === openProductSession) {
        openingProduct.value = false;
      }
    }
  }

  async function closeProduct() {
    if (savingProduct.value || resettingProduct.value) return;
    const wasOpen = editedProduct.value !== null;
    openProductSession += 1;
    openingProduct.value = false;
    productVariantStocks.value = [];
    productImageOverrides.value = [];
    colorSourceChoices.value = [];
    editedProduct.value = null;
    if (wasOpen) {
      await options.onReloadCatalog();
    }
  }

  async function resetProductToShopify() {
    if (!editedProduct.value || savingProduct.value || resettingProduct.value) {
      return;
    }
    resettingProduct.value = true;
    const productGid = editedProduct.value.id;
    try {
      const resetProduct = await resetShopifyProductOverrides(productGid);
      const patchProduct = (product: ShopifyProduct): ShopifyProduct =>
        product.id === productGid ? { ...product, ...resetProduct } : product;
      options.catalog.value = {
        ...options.catalog.value,
        items: options.catalog.value.items.map(patchProduct),
        newProducts: options.catalog.value.newProducts.map(patchProduct),
      };
      await openProduct(resetProduct);
      options.success(
        "Fiche remise aux données Shopify du moment."
      );
    } catch (caught) {
      options.error(
        caught instanceof Error
          ? caught.message
          : "Impossible de réinitialiser la fiche."
      );
    } finally {
      resettingProduct.value = false;
    }
  }

  async function saveProduct() {
    if (!editedProduct.value) return;
    if (!canSaveProductForm.value) {
      options.error(
        `Complétez les champs obligatoires : ${productFormMissingFields.value.join(
          ", ",
        )}.`,
      );
      return;
    }
    const compositions = normalizedCompositionsForSave.value;
    const { valid, message } = validateCompositionsForSave(
      compositions,
      compositionType.value,
      compositionLocations.value,
    );
    if (!valid) {
      options.error(message);
      return;
    }
    savingProduct.value = true;
    const productGid = editedProduct.value.id;
    const savedPrice = parsePositivePrice(form.price);
    const weightError = getMelUnitWeightError(form.weight);
    if (weightError) {
      options.error(weightError);
      savingProduct.value = false;
      return;
    }
    const savedWeight = parsePositiveWeight(form.weight);
    try {
      await saveShopifyProductOverrides(productGid, {
        images: productImageOverrides.value,
        brandId: Number(form.brandId) || undefined,
        categoryId: Number(form.categoryId) || undefined,
        collectionId: Number(form.collectionId) || undefined,
        provenanceId: Number(form.provenanceId) || undefined,
        price: savedPrice,
        weight: savedWeight,
        compositionId: compositions[0]?.compositionId,
        compositionLocationId: compositions[0]?.locationId,
        compositionPercentage: Number(compositions[0]?.percentage) || undefined,
        compositions,
        colorId:
          selectedColors.value.find((color) => color.isMain)?.id ??
          selectedColors.value[0]?.id,
        colorIds: selectedColors.value.map((color) => color.id),
        colorSourceChoices: colorSourceChoices.value.map((choice) => ({
          sourceName: choice.sourceName,
          colorId: choice.colorId,
        })),
        colors: selectedColors.value.map((color) => ({
          colorId: color.id,
          isMain: color.isMain,
          quantitiesBySize: color.quantitesParTaille,
          sizeStocks: color.tailleStocks
            ?.filter((item) => item.quantity != null)
            .map((item) => ({
              size: item.taille,
              quantity: Number(item.quantity) || 0,
            })),
          stock: color.stock != null ? Number(color.stock) || 0 : undefined,
        })),
        soldBy:
          form.soldBy === "couleurs" ||
          form.soldBy === "melangees" ||
          form.soldBy === "tailles"
            ? form.soldBy
            : undefined,
        packId: Number(form.packId) || undefined,
        sizeRangeId: Number(form.sizeRangeId) || undefined,
        stock:
          form.soldBy === "melangees" && form.stock !== ""
            ? Math.max(0, Number(form.stock) || 0)
            : undefined,
      });
      if (compositionType.value === "autres") {
        const byLocation = new Map<number, CompositionItem[]>();
        selectedCompositions.value.forEach((composition) => {
          const group = byLocation.get(composition.localisationId) || [];
          group.push(composition);
          byLocation.set(composition.localisationId, group);
        });
        byLocation.forEach((group) => rememberCompositionSet(group));
      }
      productImageOverrides.value = [];
      editedProduct.value = null;
      options.success("Informations MEL enregistrées.");
      await options.onReloadCatalog();
    } catch (caught) {
      options.error(
        caught instanceof Error ? caught.message : "Enregistrement impossible.",
      );
    } finally {
      savingProduct.value = false;
    }
  }

  watch(
    () => [form.price, form.weight],
    () => {
      syncProductShopifyPriceHints();
    },
  );

  watch(
    [
      () => form.soldBy,
      () => form.sizeRangeId,
      () => selectedColors.value.map((color) => color.id).join(","),
    ],
    () => {
      if (!editedProduct.value) return;
      if (form.soldBy === "tailles") {
        form.packId = "";
      } else if (
        form.packId &&
        !packOptions.value.some(
          (item) => String(item.value) === String(form.packId),
        )
      ) {
        form.packId = "";
      }
      syncColorStocks(true);
    },
  );

  const pendingColors = computed(
    () => editedProduct.value?.pendingColors ?? [],
  );

  const colorCollisions = computed(
    () => editedProduct.value?.colorCollisions ?? [],
  );

  function collisionChoice(sourceName: string) {
    const key = normalizeStockLabel(sourceName);
    return colorSourceChoices.value.find(
      (choice) => normalizeStockLabel(choice.sourceName) === key,
    );
  }

  function collisionChoiceLabel(sourceName: string) {
    const choice = collisionChoice(sourceName);
    if (!choice) return "";
    return (
      officialColors.value.find(
        (color) => Number(color.id_couleur) === choice.colorId,
      )?.couleur_FR || ""
    );
  }

  function collisionBlockMessage(): string | null {
    const groups = colorCollisions.value;
    if (!groups.length) return null;
    const buckets = new Map<number, string[]>();
    const withoutChoice: string[] = [];
    for (const group of groups) {
      for (const name of group.names) {
        const choice = collisionChoice(name);
        if (!choice) {
          withoutChoice.push(name);
          const current = buckets.get(group.colorId) ?? [];
          current.push(name);
          buckets.set(group.colorId, current);
          continue;
        }
        const current = buckets.get(choice.colorId) ?? [];
        current.push(name);
        buckets.set(choice.colorId, current);
      }
    }
    if (withoutChoice.length) {
      return `Couleurs à séparer : choisissez une couleur officielle pour ${withoutChoice.join(", ")}.`;
    }
    const shared = [...buckets.values()].filter((names) => names.length > 1);
    if (!shared.length) return null;
    return `Couleurs à séparer : ${shared
      .map((names) => names.join(", "))
      .join(" ; ")}.`;
  }

  function assignCollisionColor(sourceName: string, official: CouleurAPI) {
    const colorId = Number(official.id_couleur);
    if (!Number.isInteger(colorId) || colorId <= 0) {
      options.error("Couleur officielle invalide.");
      return;
    }
    const key = normalizeStockLabel(sourceName);
    colorSourceChoices.value = [
      ...colorSourceChoices.value.filter(
        (choice) => normalizeStockLabel(choice.sourceName) !== key,
      ),
      { sourceName, colorId },
    ];
    const groups = colorCollisions.value;
    const collapsedIds = new Set(groups.map((group) => group.colorId));
    const neededIds = new Set<number>();
    for (const group of groups) {
      for (const name of group.names) {
        const choice = collisionChoice(name);
        neededIds.add(choice?.colorId ?? group.colorId);
      }
    }
    selectedColors.value = selectedColors.value.filter(
      (color) =>
        !collapsedIds.has(Number(color.id)) || neededIds.has(Number(color.id)),
    );
    for (const neededId of neededIds) {
      if (selectedColors.value.some((color) => Number(color.id) === neededId)) {
        continue;
      }
      const known = officialColors.value.find(
        (color) => Number(color.id_couleur) === neededId,
      );
      const group = groups.find((item) => item.colorId === neededId);
      selectedColors.value = [
        ...selectedColors.value,
        {
          id: neededId,
          nom: known?.couleur_FR || group?.officialLabel || String(neededId),
          isMain: selectedColors.value.length === 0,
          hexColor: known?.hexColor || official.hexColor || null,
          quantitesParTaille:
            form.soldBy === "melangees" && selectedSizeNames.value.length
              ? new Array(selectedSizeNames.value.length).fill(0)
              : undefined,
        },
      ];
    }
    if (
      selectedColors.value.length &&
      !selectedColors.value.some((color) => color.isMain)
    ) {
      selectedColors.value = selectedColors.value.map((color, index) => ({
        ...color,
        isMain: index === 0,
      }));
    }
    dropAbandonedMappedColors();
    syncColorStocks(true);
  }

  /** La couleur d'origine disparaît quand chaque nom Shopify de la fiche a une autre couleur. */
  function dropAbandonedMappedColors() {
    selectedColors.value = selectedColors.value.filter((color) => {
      const labels = linkedSourceLabelsByColorId.value[Number(color.id)] ?? [];
      if (labels.length < 2) return true;
      const allMoved = labels.every((label) => {
        const choice = collisionChoice(label);
        return choice && Number(choice.colorId) !== Number(color.id);
      });
      return !allMoved;
    });
    if (
      selectedColors.value.length &&
      !selectedColors.value.some((color) => color.isMain)
    ) {
      selectedColors.value = selectedColors.value.map((color, index) => ({
        ...color,
        isMain: index === 0,
      }));
    }
  }

  const pendingCategories = computed(
    () => editedProduct.value?.pendingCategories ?? [],
  );

  /** Catégorie eFashion déjà choisie (manuelle ou liée) → masquer la zone optionnelle. */
  const hasEfashionCategorySelection = computed(() => {
    if (String(form.categoryId || "").trim()) return true;
    if (selectedCategoryLevel3.value) return true;
    if (
      selectedCategoryLevel2.value &&
      categoryLevel3Options.value.length === 0
    ) {
      return true;
    }
    if (
      selectedCategoryLevel1.value &&
      categoryLevel2Options.value.length === 0
    ) {
      return true;
    }
    return false;
  });

  function applyColorMappings(
    rows: Array<{ sourceLabel: string; idCouleur: number }>,
  ) {
    const next: Record<number, string[]> = {};
    for (const row of rows) {
      const id = Number(row.idCouleur);
      const label = String(row.sourceLabel || "").trim();
      if (!Number.isInteger(id) || id <= 0 || !label) continue;
      if (!next[id]) next[id] = [];
      if (!next[id].includes(label)) next[id].push(label);
    }
    linkedSourceLabelsByColorId.value = next;
  }

  async function reloadColorMappings() {
    try {
      applyColorMappings(await getShopifyColorMappings());
    } catch {
      linkedSourceLabelsByColorId.value = {};
    }
  }

  async function linkPendingColor(
    sourceLabel: string,
    officialColor: CouleurAPI,
  ) {
    if (!editedProduct.value) return;
    const colorId = Number(officialColor.id_couleur);
    if (!Number.isInteger(colorId) || colorId <= 0) {
      options.error("Couleur officielle invalide.");
      return;
    }
    try {
      await saveShopifyColorMapping(sourceLabel, colorId);
      const detailed = await getShopifyProduct(editedProduct.value.id);
      const nextProduct: ShopifyProduct = {
        ...editedProduct.value,
        ...detailed,
        overrides: {
          ...(detailed.overrides || {}),
          ...(editedProduct.value.overrides || {}),
        },
        images: detailed.images?.length
          ? detailed.images
          : editedProduct.value.images,
      };
      editedProduct.value = nextProduct;
      options.catalog.value = {
        ...options.catalog.value,
        items: options.catalog.value.items.map((item) =>
          item.id === nextProduct.id ? { ...item, ...nextProduct } : item,
        ),
        newProducts: options.catalog.value.newProducts.map((item) =>
          item.id === nextProduct.id ? { ...item, ...nextProduct } : item,
        ),
      };

      const alreadySelected = selectedColors.value.some(
        (color) => Number(color.id) === colorId,
      );
      if (!alreadySelected) {
        selectedColors.value = [
          ...selectedColors.value,
          {
            id: colorId,
            nom: officialColor.couleur_FR,
            isMain: selectedColors.value.length === 0,
            hexColor: officialColor.hexColor || null,
            quantitesParTaille:
              form.soldBy === "melangees" && selectedSizeNames.value.length
                ? new Array(selectedSizeNames.value.length).fill(0)
                : undefined,
          },
        ];
      }
      syncColorStocks(true);
      productImageOverrides.value = buildInitialImageOverrides(nextProduct);
      await reloadColorMappings();
      options.success(
        `« ${sourceLabel} » lié à « ${officialColor.couleur_FR} ».`,
      );
    } catch (caught) {
      options.error(
        caught instanceof Error
          ? caught.message
          : "Impossible d'enregistrer la liaison de couleur.",
      );
    }
  }

  async function linkPendingCategory(
    sourceLabel: string,
    path: CategoryPathSelection,
  ) {
    if (!editedProduct.value) return;
    const categoryId = Number(path.leafId);
    if (!Number.isInteger(categoryId) || categoryId <= 0) {
      options.error("Catégorie eFashion invalide.");
      return;
    }
    if (String(categoryId).length !== 6) {
      options.error("Choisissez une catégorie feuille (6 chiffres).");
      return;
    }
    try {
      await saveShopifyCategoryMapping(sourceLabel, categoryId);
      const detailed = await getShopifyProduct(editedProduct.value.id);
      const nextOverrides = {
        ...(editedProduct.value.overrides || {}),
        ...(detailed.overrides || {}),
        categoryId,
      };
      const nextProduct: ShopifyProduct = {
        ...editedProduct.value,
        ...detailed,
        overrides: nextOverrides,
        pendingCategories: detailed.pendingCategories ?? [],
        images: detailed.images?.length
          ? detailed.images
          : editedProduct.value.images,
      };
      editedProduct.value = nextProduct;
      options.catalog.value = {
        ...options.catalog.value,
        items: options.catalog.value.items.map((item) =>
          item.id === nextProduct.id ? { ...item, ...nextProduct } : item,
        ),
        newProducts: options.catalog.value.newProducts.map((item) =>
          item.id === nextProduct.id ? { ...item, ...nextProduct } : item,
        ),
      };
      form.categoryId = String(categoryId);
      restoreCategoryPath(form.categoryId);
      configureCompositionReferences(true);
      if (
        typeof nextOverrides.weight === "number" &&
        nextOverrides.weight > 0
      ) {
        form.weight = String(nextOverrides.weight);
      }
      syncAutoWeightFromCategory();
      options.success(`« ${sourceLabel} » lié à la catégorie eFashion.`);
    } catch (caught) {
      options.error(
        caught instanceof Error
          ? caught.message
          : "Impossible d'enregistrer la liaison de catégorie.",
      );
    }
  }

  return {
    editedProduct,
    savingProduct,
    resettingProduct,
    openingProduct,
    productImageOverrides,
    categoriesTree,
    selectedColors,
    pendingColors,
    colorCollisions,
    colorSourceChoices,
    collisionChoiceLabel,
    assignCollisionColor,
    pendingCategories,
    hasEfashionCategorySelection,
    selectedCompositions,
    compositionType,
    compositionLocations,
    shoeCompositions,
    compositionAutocompleteOptions,
    showCompositionDropdown,
    isLoadingCompositionAutocomplete,
    selectedCategoryLevel1,
    selectedCategoryLevel2,
    selectedCategoryLevel3,
    productShopifyPriceHint,
    productWholesalePriceHint,
    productShopifyWeightHint,
    form,
    brandOptions,
    collectionOptions,
    provenanceOptions,
    packOptions,
    sizeRangeOptions,
    categoryLevel1Options,
    categoryLevel2Options,
    categoryLevel3Options,
    selectedSizeNames,
    soldByOptions,
    melangeesPackMismatchMessage,
    productFormMissingFields,
    canSaveProductForm,
    officialColors,
    linkedSourceLabelsByColorId,
    openProduct,
    closeProduct,
    resetProductToShopify,
    saveProduct,
    linkPendingColor,
    linkPendingCategory,
    onCategoryPathSelect,
    onCategoryLevel1Change,
    onCategoryLevel2Change,
    onCategoryLevel3Change,
    onCategoryPathClear,
    handleCompositionInput,
    selectComposition,
    hideCompositionDropdown,
    onProductImagesUpdate,
    onProductImagesSaved,
    setTailleStockQuantity,
    setColorStock,
  };
}
